import {FormikErrors, FormikTouched} from 'formik';
import React, {useMemo, useRef, CSSProperties} from 'react';
import {Alert} from 'react-bootstrap';
import {Icon} from 'shared/helpers';
import {parseHtmlString} from 'shared/helpers/HTMLParser';

interface ErrorHandlerInterface<T = {[key: string]: string}> {
    show?: boolean;
    errors?: FormikErrors<T>;
    touched: FormikTouched<T>;
    externalErrors?: string[];
    onClose?: () => void;
    style?: CSSProperties;
    className?: string;
}

const emptyErrors: string[] = [];
const emptyFunction = () => {
    //
};

// eslint-disable-next-line require-jsdoc -- don't need jsdoc as we are using ts
export function ErrorHandler<T>({
    show = true,
    errors,
    touched,
    externalErrors = emptyErrors,
    onClose = emptyFunction,
    ...attr
}: ErrorHandlerInterface<T>) {
    const errorMessages = useMemo(() => {
        const messages: string[] = [];

        if (Array.isArray(externalErrors) && externalErrors.length) {
            externalErrors.forEach((error) => messages.push(error));
        }

        Object.keys(errors).forEach((key) => {
            if (touched[key as keyof T] && errors[key as keyof T]) {
                messages.push(errors[key as keyof T] as string);
            }
        });

        return messages;
    }, [externalErrors, errors, touched]);

    const style = useRef({
        width: '300px',
        height: 'auto',
        minHeight: '70px',
        borderRadius: '9px',
        paddingLeft: '10px',
    }).current;

    if (errorMessages.length) {
        return (
            <Alert
                className="cbc-top-message"
                style={style}
                variant="danger"
                {...attr}
                show={show}
                onClose={onClose}
                dismissible={typeof onClose == 'function'}>
                <Icon iconName="Button-Error.svg" />
                <div style={{whiteSpace: 'pre-line'}}>
                    {errorMessages.map((error) => {
                        return <div key={error}>{parseHtmlString(error)}</div>;
                    })}
                </div>
            </Alert>
        );
    }

    return null;
}
